import store from 'src/store/index';
import { localStorageService } from 'src/services/local-storage.service';
import { AuthService, AuthRequest, roleLegacyMapping } from '.';

export async function getUserDataFromToken(
  token: string,
  toWl: boolean = false
) {
  const getMeResponse = await AuthService.getMe(token);

  if ('active_account' in getMeResponse) {
    // retorna dado no formato customers: [deprecated_customer_id], para manter compatibilidade com o login já utilizado ao se conectar ao laravel
    // file: src/store/modules/auth/sagas.js fn: loginRequest
    const customers = [
      getMeResponse.active_account?.metadata?.deprecated_customer_id,
      ...getMeResponse.accounts
        .filter(
          (account) =>
            account.metadata?.deprecated_customer_id !==
            getMeResponse.active_account?.metadata?.deprecated_customer_id
        )
        .map((account) => account.metadata?.deprecated_customer_id),
    ].filter(Boolean);

    const roles = getMeResponse.roles
      .map((role) => roleLegacyMapping[role.name])
      .filter((roleId): roleId is number => roleId !== undefined);

    if (toWl) {
      return {
        user: {
          id: getMeResponse.metadata.deprecated_user_id,
          email: getMeResponse.email,
          customers,
          roles,
        },
      };
    }

    return {
      customers,
      roles,
    };
  }

  return null;
}

export async function HeimdallBFFLoginWithToken(payload: {
  token: string;
  customer_id: string;
}) {
  try {
    const response = await AuthService.loginWithToken(payload);

    return response;
  } catch (error) {
    console.error('Erro ao fazer login com o Heimdall', error);
    return null;
  }
}

export async function getFoundationAllowed() {
  try {
    const info = localStorageService.getLocalStorageItem({
      key: 'user_token_login_info',
    });

    // se não tiver token, não tem como verificar se o usuário tem permissão para usar a nova tela de chats
    if (!info) {
      return null;
    }

    const foundationAllowed = await AuthService.getFoundationAllowed(
      info?.token ?? ''
    );
    const newInterfaceUrl =
      store.getState()?.whitelabel?.config?.new_interface_url;

    // se não tiver permissão ou não tiver url da nova tela de chats, não tem como redirecionar para a nova tela de chats
    if (!foundationAllowed || !newInterfaceUrl) {
      return null;
    }

    // monta a url de redirecionamento para a nova tela de chats
    const pathToLoginAndRedirect = `${newInterfaceUrl}/redirect?sessionId=${encodeURIComponent(
      info?.session_id ?? ''
    )}&toPath=${encodeURIComponent(`${newInterfaceUrl}/chat`)}`;

    return pathToLoginAndRedirect;
  } catch (error) {
    console.error(
      'Erro ao verificar permissão de uso da nova tela de chats',
      error
    );
    return null;
  }
}

export async function HeimdallCheckSessionById(sessionId: string) {
  try {
    const response = await AuthService.checkSessionById(sessionId);

    return response;
  } catch (error) {
    console.error('Erro ao verificar sessão', error);
    return null;
  }
}

export async function HeimdallBFFLogin(
  data: Omit<AuthRequest, 'device_name' | 'device_token'>
) {
  try {
    const authResponse = await AuthService.getToken(data);

    if ('token' in authResponse) {
      const userData = await getUserDataFromToken(authResponse.token);

      await localStorageService.setLocalStorageItem({
        key: 'user_token_login_info',
        value: 'legacy_token' in authResponse ? authResponse : null,
      });

      if (userData) {
        // retorna dado no formato token: legacy_token, customers: [deprecated_customer_id], roles: [role_id] para manter compatibilidade com o login já utilizado ao se conectar ao laravel
        // file: src/store/modules/auth/sagas.js fn: loginRequest
        return {
          token: authResponse.legacy_token,
          user: userData,
          session_id: authResponse.session_id,
        };
      }
    }

    return {
      error: 'Erro ao fazer login',
      message: 'Erro ao fazer login',
    };
  } catch (error) {
    console.error('Erro ao fazer login com o Heimdall', error);
    return {
      error: 'Erro ao fazer login',
      message: 'Erro ao fazer login',
    };
  }
}

export async function HeimdallBFFLoginWithSessionId(sessionId: string) {
  try {
    const response = await HeimdallCheckSessionById(sessionId);

    if (!response || !('legacy_token' in response)) {
      return {
        error: 'Erro ao verificar sessão',
        message: 'Erro ao verificar sessão',
      };
    }

    const value = 'legacy_token' in response ? response : null;

    if (!value) {
      return {
        error: 'Erro ao verificar sessão',
        message: 'Dados de sessão inválidos',
      };
    }

    // Validação explícita do refresh_token antes de salvar
    if (!value.refresh_token || typeof value.refresh_token !== 'string' || value.refresh_token.trim() === '') {
      return {
        error: 'Erro ao verificar sessão',
        message: 'Refresh token não encontrado na resposta da sessão',
      };
    }

    // Validação dos outros campos essenciais
    if (!value.token || typeof value.token !== 'string' || value.token.trim() === '') {
      return {
        error: 'Erro ao verificar sessão',
        message: 'Token não encontrado na resposta da sessão',
      };
    }

    if (!value.legacy_token || typeof value.legacy_token !== 'string' || value.legacy_token.trim() === '') {
      return {
        error: 'Erro ao verificar sessão',
        message: 'Legacy token não encontrado na resposta da sessão',
      };
    }

    // Salva os dados completos do token (incluindo refresh_token) no localStorage
    localStorageService.setLocalStorageItem({
      key: 'user_token_login_info',
      value,
    });

    // Verifica se foi salvo corretamente
    const savedValue = localStorageService.getLocalStorageItem({
      key: 'user_token_login_info',
    });

    if (!savedValue || !savedValue.refresh_token || savedValue.refresh_token.trim() === '') {
      return {
        error: 'Erro ao salvar dados de autenticação',
        message: 'Não foi possível salvar o refresh token',
      };
    }

    if (value.legacy_token && value.token) {
      const userData = await getUserDataFromToken(value.token);

      if (userData) {
        // retorna dado no formato token: legacy_token, para manter compatibilidade com o login já utilizado ao se conectar ao laravel
        // file: src/store/modules/auth/sagas.js fn: loginRequest
        return {
          token: value.legacy_token,
          user: userData,
          session_id: value.session_id,
        };
      }
      return {
        error: 'Erro ao obter dados do usuário',
        message: 'Não foi possível obter os dados do usuário',
      };

    }

    return {
      error: 'Erro ao verificar sessão',
      message: 'Token ou legacy_token não encontrado na resposta',
    };

  } catch (error) {
    console.error('Erro ao verificar sessão', error);
    return {
      error: 'Erro ao verificar sessão',
      message: 'Erro ao verificar sessão',
    };
  }
}
